import axios from "axios";
import dayjs from "dayjs";
import Cookies from "js-cookie";

import {
  currentBankDurationOptionsMap,
  currentCompanyDurationOptionsMap,
} from "./const";
import { convertDateFormat } from "./functions";
import { getClickId } from "./callApi";

/**
 * InterateLeads LMS API client.
 * All lead traffic (progressive capture, finalize/status check, bank & address
 * lookup) goes through this module. Credentials & base URL come from env so they
 * can be swapped without touching code.
 *
 *   REACT_APP_LMS_API_BASE_URL   e.g. https://lms.interateleads.com
 *   REACT_APP_LMS_API_ID         publisher apiId
 *   REACT_APP_LMS_API_PASSWORD   publisher apiPassword
 */
export const LMS_BASE_URL =
  process.env.REACT_APP_LMS_API_BASE_URL || "https://lms.interateleads.com";
export const LMS_API_ID = process.env.REACT_APP_LMS_API_ID || "";
export const LMS_API_PASSWORD = process.env.REACT_APP_LMS_API_PASSWORD || "";

export const lmsRequest = axios.create({
  baseURL: `${LMS_BASE_URL}/api`,
});

// ---------------------------------------------------------------------------
// Value helpers
// ---------------------------------------------------------------------------

const digitsOnly = (value) =>
  value === undefined || value === null ? value : String(value).replace(/\D/g, "");

const toYesNo = (value) => {
  if (value === undefined || value === null || value === "") return undefined;
  const val = String(value).toUpperCase();
  if (["YES", "1", "TRUE", "OWN", "Y"].includes(val)) return "YES";
  if (["NO", "0", "FALSE", "RENT", "N"].includes(val)) return "NO";
  return val;
};

const convertBankDuration = (value) => {
  if (value === undefined || value === null || value === "") return undefined;
  if (isNaN(value)) return currentBankDurationOptionsMap[value] ?? value;
  return value;
};

const convertCompanyDuration = (value) => {
  if (value === undefined || value === null || value === "") return undefined;
  if (isNaN(value)) return currentCompanyDurationOptionsMap[value] ?? value;
  return value;
};

const formatDob = (dob) => {
  if (!dob) return undefined;
  try {
    return dayjs(convertDateFormat(String(dob))).format("YYYY-MM-DD");
  } catch {
    return dob;
  }
};

// Full US state name -> 2 letter code (address-lookup returns full names).
const US_STATES = {
  alabama: "AL", alaska: "AK", arizona: "AZ", arkansas: "AR", california: "CA",
  colorado: "CO", connecticut: "CT", delaware: "DE", "district of columbia": "DC",
  florida: "FL", georgia: "GA", hawaii: "HI", idaho: "ID", illinois: "IL",
  indiana: "IN", iowa: "IA", kansas: "KS", kentucky: "KY", louisiana: "LA",
  maine: "ME", maryland: "MD", massachusetts: "MA", michigan: "MI",
  minnesota: "MN", mississippi: "MS", missouri: "MO", montana: "MT",
  nebraska: "NE", nevada: "NV", "new hampshire": "NH", "new jersey": "NJ",
  "new mexico": "NM", "new york": "NY", "north carolina": "NC",
  "north dakota": "ND", ohio: "OH", oklahoma: "OK", oregon: "OR",
  pennsylvania: "PA", "rhode island": "RI", "south carolina": "SC",
  "south dakota": "SD", tennessee: "TN", texas: "TX", utah: "UT",
  vermont: "VT", virginia: "VA", washington: "WA", "west virginia": "WV",
  wisconsin: "WI", wyoming: "WY", "puerto rico": "PR",
};

export const stateToAbbr = (state) => {
  if (!state) return state;
  const s = String(state).trim();
  if (s.length === 2) return s.toUpperCase();
  return US_STATES[s.toLowerCase()] ?? s;
};

// ---------------------------------------------------------------------------
// Contact (internal redux shape) -> InterateLeads lead fields.
// Only defined (non-undefined) values are returned so partial merges stay clean.
// ---------------------------------------------------------------------------

export const mapContactToLeadFields = (contact = {}) => {
  const storedParams = new URLSearchParams(
    sessionStorage.getItem("url_params") || ""
  );

  const raw = {
    firstName: contact.firstName,
    lastName: contact.lastName,
    email: contact.email,
    homePhone: contact.homePhone ?? contact.phone,
    cellPhone: contact.cellPhone ?? contact.phone,
    workPhone: contact.workPhone ?? contact.employerPhone,
    dob: formatDob(contact.dob),
    ssn: digitsOnly(contact.ssn ?? contact.socialSecurityNumber),
    address: contact.address ?? contact.streetAddress,
    addressLengthMonths: contact.addressLengthMonths,
    city: contact.city,
    state: stateToAbbr(contact.state),
    zip: contact.zip ?? contact.zipCode,
    loanAmount:
      contact.loanAmount ?? contact.amount ?? contact.loan_amount,
    creditScore: contact.creditScore ?? contact.creditScoreRating,
    incomeType: contact.incomeType ?? contact.incomeSource,
    incomePaymentFrequency: contact.incomePaymentFrequency,
    incomeNetMonthly: contact.incomeNetMonthly ?? contact.monthlyIncome,
    incomeNextDate1: contact.incomeNextDate1,
    incomeNextDate2: contact.incomeNextDate2,
    jobTitle: contact.jobTitle,
    workCompanyName:
      contact.workCompanyName ??
      contact.work_company_name ??
      contact.employerName,
    workTimeAtEmployer: convertCompanyDuration(contact.workTimeAtEmployer),
    bankAba: contact.bankAba ?? contact.routingNumber ?? contact.bank_aba,
    bankName: contact.bankName,
    bankAccountNumber: contact.bankAccountNumber ?? contact.bankAccount,
    bankAccountType: contact.bankAccountType,
    bankDirectDeposit: toYesNo(
      contact.bankDirectDeposit ?? contact.getDirectDeposit
    ),
    bankAccountLengthMonths: convertBankDuration(
      contact.currentBankDuration ?? contact.bankAccountLengthMonths
    ),
    driversLicenseNumber:
      contact.driversLicenseNumber ?? contact.licenseID,
    driversLicenseState: stateToAbbr(
      contact.driversLicenseState ?? contact.issuingState
    ),
    activeMilitary:
      toYesNo(contact.activeMilitary) ??
      (contact.incomeType === "MILITARY" ? "YES" : undefined),
    ownHome: toYesNo(
      contact.ownHome ?? contact.own_home ?? contact.residenceOwnership
    ),
    own_car: toYesNo(contact.own_car ?? contact.ownCar),
    contactTime: contact.contactTime ?? contact.bestTimeContact,
    loanReason: contact.loanReason ?? contact.loan_reason,
    debt_amount: contact.debt_amount ?? contact.unsecuredDebtAmount,
    time_at_residence:
      contact.time_at_residence ?? contact.currentResidenceDuration,
    userIp: contact.userIp ?? contact.user_ip,
    userAgent: contact.userAgent,
    consentEmailSms: "YES",
    consentToFcra: "YES",
    confirm_pii: contact.ssn ? digitsOnly(contact.ssn) : undefined,
    jornayaLeadId: contact.jornayaLeadId,
    trustedFormURL: sessionStorage.getItem("trustedFormURL") || undefined,
    // Tracking / attribution
    source: sessionStorage.getItem("utm_medium") || undefined,
    platform: sessionStorage.getItem("utm_campaign") || undefined,
    utm_id: sessionStorage.getItem("utm_id") || undefined,
    utm_medium: sessionStorage.getItem("utm_medium") || undefined,
    aff_id: storedParams.get("pubs") || undefined,
    source_id: sessionStorage.getItem("offerid") || sessionStorage.getItem("offer_id") || undefined,
    offer_id:  sessionStorage.getItem("offer_id") || sessionStorage.getItem("offerid") || undefined,
    ad_id: sessionStorage.getItem("ad_id") || undefined,
    vl_cid: Cookies.get("vl-cid") || undefined,
    fbc: Cookies.get("_fbc") || undefined,
    fbp: Cookies.get("_fbp") || undefined,
    webSiteUrl: window.location.protocol + "//" + window.location.hostname,
  };

  const clickId = getClickId(sessionStorage.getItem("utm_campaign"));
  if (clickId) raw.clickid = clickId;

  const testMode = sessionStorage.getItem("testMode");
  const testSold = sessionStorage.getItem("testSold");
  if (testMode) raw.testMode = testMode;
  if (testSold) raw.testSold = testSold;

  // Strip undefined / empty-string so we never overwrite saved fields with blanks.
  const out = {};
  Object.keys(raw).forEach((key) => {
    const value = raw[key];
    if (value !== undefined && value !== null && value !== "") out[key] = value;
  });
  return out;
};

// ---------------------------------------------------------------------------
// Endpoints
// ---------------------------------------------------------------------------

const withAuth = (payload) => ({
  apiId: LMS_API_ID,
  apiPassword: LMS_API_PASSWORD,
  ...payload,
});

/** Progressive capture. Email step: omit partialId. Later fields: pass partialId. */
export const submitPartial = (fields, partialId) => {
  const payload = withAuth({ ...fields });
  if (partialId) payload.partial_id = partialId;
  return lmsRequest.post("/lead/partial", payload);
};

/** Finalize a capture (checkKey = partial_id) or poll a lead (checkKey = lead_id). */
export const checkLeadStatus = (checkKey) =>
  lmsRequest.post(
    "/lead/check-lead-status",
    withAuth({ checkKey })
  );

/** Resolve bank name from a 9-digit ABA / routing number. */
export const bankLookup = async (aba) => {
  const res = await lmsRequest.get("/bank-lookup", { params: { aba } });
  return res?.data?.data?.bankName || "";
};

/** Validate a US ZIP -> { city, state (2-letter) } or null. */
export const zipLookup = async (zip) => {
  const res = await lmsRequest.get("/address-lookup", { params: { zip } });
  const data = res?.data?.data;
  if (data?.valid && data?.address) {
    return {
      city: data.address.city,
      state: stateToAbbr(data.address.state),
      zip: data.zip,
    };
  }
  return null;
};

/** Address autocomplete -> array of suggestion strings. */
export const addressAutocomplete = async (q) => {
  const res = await lmsRequest.get("/address-lookup", { params: { q } });
  const suggestions = res?.data?.data?.suggestions || [];
  return suggestions.map((s) => s.text);
};
